import { SortLookup } from '@/content/templates/template.provider';

export const DEFAULT_CATEGORY_SLUG = 'recommended';

export const DisplayedSlugs = [
	DEFAULT_CATEGORY_SLUG,
	'project-management',
	'software-development-and-it',
	'product-management',
	'marketing-and-sales',
	'design',
	'finance-and-operations',
	'human-resources',
] as const;

export const AdditionalSlugs = [
	'project-planning',
	'business-strategy',
	'productivity',
	'docs-and-reports',
	'startup',
	'personal',
] as const;

export type DisplayedCategoryKey = (typeof DisplayedSlugs)[number];
export type AdditionalCategoryKey = (typeof AdditionalSlugs)[number];

export type CategoryKey = DisplayedCategoryKey | AdditionalCategoryKey;

export const CategoryOrder: { [key in CategoryKey]: number } = {
	[DEFAULT_CATEGORY_SLUG]: 1,
	'project-management': 2,
	'software-development-and-it': 3,
	'product-management': 4,
	'marketing-and-sales': 5,
	design: 6,
	'finance-and-operations': 7,
	'human-resources': 8,
	// Additional categories
	'project-planning': 9,
	'business-strategy': 10,
	productivity: 11,
	'docs-and-reports': 12,
	startup: 13,
	personal: 14,
} as const;

export const CategoryTemplateOrder: {
	[key in CategoryKey]: SortLookup;
} = {
	[DEFAULT_CATEGORY_SLUG]: {
		'strategic-plan': 1,
		'project-plan': 2,
		flowchart: 3,
		'product-requirements': 4,
		'retrospective-whiteboard': 5,
		'vision-to-values': 6,
		brainstorming: 7,
		'business-status-update': 8,
		brainwriting: 9,
		'annual-plan-one-pager': 10,
		'product-roadmap': 11,
		'smart-goals': 12,
		'marketing-plan': 13,
		okrs: 14,
		'competitive-analysis': 15,
		'business-plan-one-pager': 16,
		'swot-analysis': 17,
		retrospective: 18,
		'meeting-notes': 19,
		'marketing-campaign': 20,
		decision: 21,
		'master-project-documentation': 22,
		'marketing-united': 23,
		'daily-stand-up': 24,
		'software-architecture-review': 25,
		'product-launch': 26,
		'devops-runbook': 27,
		'sales-account-planning': 28,
	},
	'project-management': {
		'project-plan': 1,
		'strategic-plan': 2,
		'simple-retrospective': 3,
		'retrospective-whiteboard': 3,
		'vision-to-values': 4,
		brainstorming: 5,
		'product-vision-board': 6,
		'business-status-update': 7,
		'project-status': 8,
		'project-poster': 9,
		'smart-goals': 10,
		'kanban-board': 11,
		'kanban-board-whiteboard': 11,
		okrs: 12,
		'competitive-analysis': 13,
		'swot-analysis': 14,
		retrospective: 15,
		'meeting-notes': 16,
		'prioritization-matrix-whiteboard': 17,
		decision: 18,
		'master-project-documentation': 19,
		'daily-stand-up': 20,
		'4-ls-retro': 21,
		'4-ls-retro-whiteboard': 21,
		'team-poster': 22,
		'to-do-list': 23,
		'daily-sync': 24,
		'daily-sync-whiteboard': 24,
		'team-homepage': 25,
		'end-of-week-status-report': 26,
		'sprint-planning': 27,
		'sprint-planning-whiteboard': 27,
		'1-on-1-meeting': 28,
		'weekly-meeting-notes': 29,
		'roles-and-responsibilities': 30,
		timeline: 31,
		'timeline-whiteboard': 31,
		'my-user-manual': 32,
		'workshop-planner': 33,
		swimlanes: 34,
		'swimlane-diagram': 34,
		'5-whys-analysis': 35,
		'transition-plan': 36,
		'4-ls-retrospective': 37,
		'elevator-pitch-whiteboard': 38,
		'out-of-office-plan': 39,
		'project-kickoff': 40,
		'lean-coffee': 41,
		'risk-assessment': 42,
		'mvp-ideation': 43,
		'change-management-plan': 44,
		'90-day-plan': 45,
		'2x2-prioritization-method': 46,
		'dependency-mapping': 47,
		'work-trip-planner': 48,
		'mutual-action-plan': 49,
		'timeline-workflow': 50,
		'all-hands-meeting': 51,
		'design-your-ideal-workweek': 52,
		'voting-table': 53,
		'pi-planning': 54,
		'quarterly-check-in': 55,
		'working-agreements': 56,
		premortem: 57,
		'service-blueprint': 58,
		'executive-business-review': 59,
		'year-in-review': 60,
		'disruptive-brainstorming': 61,
		workflow: 62,
		'workflow-whiteboard': 62,
		'introductory-blog': 63,
		'project-team-health-monitor': 64,
		'presenter-mode': 65,
		'ooo-team-notification': 66,
		'virtual-event': 67,
		'ritual-reset': 68,
	},
	'software-development-and-it': {
		'product-requirements': 1,
		'kanban-board': 2,
		'kanban-board-whiteboard': 2,
		'product-roadmap': 3,
		retrospective: 4,
		'software-architecture-review': 5,
		'incident-postmortem-whiteboard': 6,
		'how-to-article': 7,
		'it-project-poster': 8,
		'simple-retrospective': 9,
		'retrospective-whiteboard': 9,
		'itsm-runbook': 10,
		'concept-map': 11,
		'itsm-known-errors': 12,
		'daily-sync': 13,
		'daily-sync-whiteboard': 13,
		'sprint-planning-meeting': 14,
		workflow: 15,
		'workflow-whiteboard': 15,
		'experiment-plan-and-results': 16,
		'capacity-planning': 17,
		'pi-planning': 18,
		'incident-communication': 19,
		'sprint-planning': 20,
		'sprint-planning-whiteboard': 20,
		'itsm-change-management': 21,
		'4-ls-retro': 22,
		'4-ls-retro-whiteboard': 22,
		'risk-assessment': 23,
		'timeline-workflow': 24,
		'mvp-ideation': 25,
		'flow-chart': 26,
		flowchart: 26,
		'aws-architecture-diagram': 27,
		'troubleshooting-article': 28,
		'itsm-post-incident-review': 29,
		'product-roadmap-whiteboard': 30,
		'design-your-ideal-workweek': 31,
		'working-agreements': 32,
		'product-vision-board': 33,
		'incident-postmortem': 34,
		rice: 35,
		'itsm-weekly-major-incident-report': 36,
		'service-blueprint': 37,
		'devops-change-management': 38,
		timeline: 39,
		'timeline-whiteboard': 39,
		'project-team-health-monitor': 40,
		'service-team-health-monitor': 41,
	},
	'product-management': {
		'strategic-plan': 1,
		'product-requirements': 2,
		'product-roadmap': 3,
		brainwriting: 4,
		'lennys-product-requirements': 5,
		'product-roadmap-whiteboard': 6,
		okrs: 7,
		'daily-stand-up': 8,
		'daily-sync': 9,
		'daily-sync-whiteboard': 9,
		'product-launch': 10,
		'kanban-board': 11,
		'kanban-board-whiteboard': 11,
		'simple-retrospective': 12,
		'retrospective-whiteboard': 12,
		'how-to-article': 13,
		rice: 14,
		'sprint-planning-meeting': 15,
		'lean-coffee': 16,
		'team-homepage': 17,
		'network-of-teams': 18,
		'team-mapping': 18,
		'experiment-plan-and-results': 19,
		'product-vision-board': 20,
		'capacity-planning': 21,
		'end-of-week-status-report': 22,
		'4-ls-retro': 23,
		'4-ls-retro-whiteboard': 23,
		'weekly-meeting-notes': 24,
		workflow: 25,
		'workflow-whiteboard': 25,
		'customer-journey-mapping': 26,
		'project-kickoff': 27,
		'mvp-ideation': 28,
		timeline: 29,
		'timeline-whiteboard': 29,
		'dependency-mapping': 30,
		'project-poster': 31,
		'timeline-workflow': 32,
		'customer-interview-report': 33,
		'startup-pitch-deck': 34,
		'pi-planning': 35,
		'voting-table': 36,
		'concept-map': 37,
		'working-agreements': 38,
		'crazy-8s-brainstorming': 39,
		'elevator-pitch': 40,
		'gap-analysis': 41,
		'executive-business-review': 42,
		'user-story-map': 43,
		'prioritization-matrix': 44,
		'sailboat-retro': 45,
		'sailboat-retrospective': 45,
		'year-in-review': 46,
		'problem-framing': 47,
		'goals-signals-measures': 48,
	},
	'marketing-and-sales': {
		'marketing-plan': 1,
		'business-status-update': 2,
		'strategic-plan': 3,
		brainwriting: 4,
		'competitive-analysis': 5,
		'business-plan-one-pager': 6,
		'timeline-workflow': 7,
		'swot-analysis': 8,
		'content-strategy': 9,
		'meeting-notes': 10,
		'annual-calendar': 11,
		'marketing-campaign': 12,
		'user-story-map': 13,
		'master-project-documentation': 14,
		'gap-analysis': 15,
		'marketing-united': 16,
		'product-launch': 17,
		timeline: 18,
		'timeline-whiteboard': 18,
		'sales-account-planning': 19,
		'disruptive-brainstorm': 20,
		'disruptive-brainstorm-whiteboard': 20,
		'customer-journey-mapping': 21,
		'email-drip': 22,
		'crazy-8s-brainstorming': 23,
		'safe-lean-business-case': 24,
		'customer-interview-report': 25,
		'ice-breakers': 26,
		'creative-brief': 27,
		'dot-voting': 28,
		'message-house': 29,
		'sales-portal': 30,
		'elevator-pitch-whiteboard': 31,
		'stakeholder-communications': 32,
		'prioritization-matrix-whiteboard': 33,
		'customer-impact-assessment': 34,
		'sticky-note-pack': 35,
		'marketing-blog-post': 36,
		'presenter-mode': 37,
		'qtc-acquisition': 38,
		'ritual-reset': 39,
	},
	design: {
		'design-system': 1,
		'content-strategy': 2,
		'concept-map': 3,
		'design-sprint': 4,
		'flow-chart': 5,
		flowchart: 5,
		'design-decision': 6,
		'sticky-note-pack': 7,
		persona: 8,
		'email-drip': 9,
		'product-roadmap-whiteboard': 10,
		brainwriting: 11,
		'writing-guidelines': 12,
		'4-ls-retro': 13,
		'4-ls-retro-whiteboard': 13,
		'disruptive-brainstorm': 14,
		'disruptive-brainstorm-whiteboard': 14,
		'design-component': 15,
		'user-story-map': 16,
		'annual-calendar': 17,
		'product-vision-board': 18,
		'design-review': 19,
		'sailboat-retro': 20,
		'sailboat-retrospective': 20,
		'content-design-glossary': 21,
		timeline: 22,
		'timeline-whiteboard': 22,
		'creative-brief': 23,
		'2x2-prioritization-method': 24,
		'message-house': 25,
		'crazy-8s-brainstorming': 26,
		'ice-breakers': 27,
		'disruptive-brainstorming': 28,
		'dot-voting': 29,
		sparring: 30,
		'org-chart': 31,
	},
	'finance-and-operations': {
		'business-status-update': 1,
		'annual-plan-one-pager': 2,
		'competitive-analysis': 3,
		'annual-calendar': 4,
		'business-plan-one-pager': 5,
		'swot-analysis': 6,
		'master-project-documentation': 7,
		'sticky-note-pack': 8,
		'sales-account-planning': 9,
		'team-poster': 10,
		'to-do-list': 11,
		'project-status': 12,
		'service-blueprint': 13,
		'roles-and-responsibilities': 14,
		'change-management-plan': 15,
		'gap-analysis': 16,
		'safe-lean-business-case': 17,
		'mutual-action-plan': 18,
		'all-hands-meeting': 19,
		'startup-pitch-deck': 20,
		rice: 21,
		'sales-portal': 22,
		'elevator-pitch': 23,
		'lean-coffee': 24,
		'executive-business-review': 25,
		'stakeholder-communications': 26,
		'customer-impact-assessment': 27,
		timeline: 28,
		'timeline-whiteboard': 28,
		'vendor-agreement': 29,
		'remote-team-meeting': 30,
		'problem-framing': 31,
		'leadership-team-health': 32,
		'fundraising-database': 33,
		'qtc-acquisition': 34,
	},
	'human-resources': {
		'team-poster': 1,
		'career-development-plan': 2,
		'annual-calendar': 3,
		'1-on-1-meeting': 4,
		'workshop-planner': 5,
		'org-chart': 6,
		'transition-plan': 7,
		'lennys-performance-review': 8,
		'ice-breakers': 9,
		'4-ls-retrospective': 10,
		'dot-voting': 11,
		'out-of-office-plan': 12,
		'90-day-plan': 13,
		'2x2-prioritization-method': 14,
		'hr-knowledge-base': 15,
		'performance-improvement-plan': 16,
		'team-formation-guide': 17,
		'team-charter': 17,
		'employee-handbook': 18,
		'work-trip-planner': 19,
		'all-hands-meeting': 20,
		timeline: 21,
		'timeline-whiteboard': 21,
		'hiring-process': 22,
		'daily-sync': 23,
		'daily-sync-whiteboard': 23,
		'quarterly-check-in': 24,
		'interview-feedback': 25,
		'network-of-teams': 26,
		'team-mapping': 26,
		'job-description': 27,
		'job-offer-letter': 28,
		'sticky-note-pack': 29,
		'introductory-blog': 30,
		'employee-promotion-letter': 31,
		swimlanes: 32,
		'swimlane-diagram': 32,
		'virtual-event': 33,
		'virtual-team-lunch': 34,
	},
	// Additional categories
	productivity: {
		'to-do-list': 1,
		'end-of-week-status-report': 2,
		'daily-stand-up': 3,
		'dependency-mapping': 4,
		'my-user-manual': 5,
		'meeting-notes': 6,
		'weekly-meeting-notes': 7,
		'smart-goals': 8,
		'1-on-1-meeting': 9,
		retrospective: 10,
		'mutual-action-plan': 11,
		'5-whys-analysis': 12,
		'working-agreements': 13,
		decision: 14,
		'problem-framing': 15,
		'voting-table': 16,
		'out-of-office-plan': 17,
		'workshop-planner': 18,
		'all-hands-meeting': 19,
		'remote-team-meeting': 20,
		'4-ls-retrospective': 21,
		'work-trip-planner': 22,
		'itsm-post-incident-review': 23,
		'virtual-event': 24,
	},
	startup: {
		'business-plan-one-pager': 1,
		'project-plan': 2,
		'startup-pitch-deck': 3,
		'product-requirements': 4,
		'team-homepage': 5,
		'strategic-plan': 6,
		'product-launch': 7,
		'roles-and-responsibilities': 8,
		'mvp-ideation': 9,
		okrs: 10,
		'project-kickoff': 11,
		'end-of-week-status-report': 12,
		brainstorming: 13,
		'swot-analysis': 14,
		'fundraising-database': 15,
		'risk-assessment': 16,
		'executive-business-review': 17,
		'problem-framing': 18,
		'smart-goals': 19,
		'goals-signals-measures': 20,
		'business-status-update': 21,
		'competitive-analysis': 22,
		'elevator-pitch': 23,
		'working-agreements': 24,
		'experiment-plan-and-results': 25,
		'year-in-review': 26,
		'daily-stand-up': 27,
		'5-whys-analysis': 28,
		'vision-to-values': 29,
		'1-on-1-meeting': 30,
		'introductory-blog': 31,
		'workshop-planner': 32,
		'remote-team-meeting': 33,
		sparring: 34,
		'all-hands-meeting': 35,
		'virtual-team-lunch': 36,
		'4-ls-retrospective': 37,
		'qtc-acquisition': 38,
	},
	'business-strategy': {
		'strategic-plan': 1,
		'business-plan-one-pager': 2,
		'customer-journey-mapping': 3,
		'change-management-plan': 4,
		'swot-analysis': 5,
		okrs: 6,
		'business-status-update': 7,
		'smart-goals': 8,
		'roles-and-responsibilities': 9,
		'executive-business-review': 10,
		'mvp-ideation': 11,
		'startup-pitch-deck': 12,
		'annual-plan-one-pager': 13,
		'5-whys-analysis': 14,
		'elevator-pitch': 15,
		'problem-framing': 16,
		'vision-to-values': 17,
		'risk-assessment': 18,
		'leadership-team-health-monitor': 19,
		'vendor-agreement': 20,
		'all-hands-meeting': 21,
		'safe-lean-business-case': 22,
		'ritual-reset': 23,
		'itsm-change-management': 24,
		'4-ls-retrospective': 25,
		'fundraising-database': 26,
		'customer-impact-assessment': 27,
		'marketing-united': 28,
	},
	'project-planning': {
		'project-plan': 1,
		'product-roadmap': 2,
		'master-project-documentation': 3,
		'dependency-mapping': 4,
		'project-status': 5,
		'end-of-week-status-report': 6,
		'project-kickoff': 7,
		'weekly-meeting-notes': 8,
		'project-poster': 9,
		'roles-and-responsibilities': 10,
		'daily-stand-up': 11,
		'capacity-planning': 12,
		'product-launch': 13,
		'change-management-plan': 14,
		retrospective: 15,
		'business-status-update': 16,
		'it-project-poster': 17,
		okrs: 18,
		'transition-plan': 19,
		'working-agreements': 19,
		'mvp-ideation': 20,
		'mutual-action-plan': 21,
		'smart-goals': 22,
		'goals-signals-measures': 23,
		brainstorming: 24,
		'workshop-planner': 25,
		decision: 26,
		'team-poster': 27,
		premortem: 28,
		'safe-lean-business-case': 29,
		'startup-pitch-deck': 30,
		'remote-team-meeting': 31,
		'ooo-team-notification': 32,
		'virtual-event': 33,
	},
	'docs-and-reports': {
		'master-project-documentation': 1,
		'how-to-article': 2,
		'hr-knowledge-base': 3,
		'writing-guidelines': 4,
		'roles-and-responsibilities': 5,
		'design-component': 6,
		'design-your-ideal-workweek': 7,
		'troubleshooting-article': 8,
		'design-review': 9,
		'itsm-weekly-major-incident-report': 10,
		'vendor-agreement': 11,
		'itsm-known-errors': 12,
		'qtc-acquisition': 13,
	},
	personal: {
		'to-do-list': 1,
		'my-user-manual': 2,
		'work-trip-planner': 3,
		'out-of-office-plan': 4,
		'year-in-review': 5,
		'transition-plan': 6,
		'quarterly-check-in': 7,
		'introductory-blog': 8,
		'ooo-team-notification': 9,
	},
} as const;
